import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BookMarked, Trash2, Pencil, Check, X, Copy } from "lucide-react";

const TYPE_LABEL = { email: "Email", call_script: "Script d'appel", relance: "Relance", offer: "Offre" };
const TYPE_COLOR = {
  email: "bg-cyan-500/10 text-cyan-300 border-cyan-500/20",
  call_script: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  relance: "bg-purple-500/10 text-purple-300 border-purple-500/20",
  offer: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
};

export default function Templates() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ name: "", content: "" });

  const load = () => api.get("/templates").then((r) => setItems(r.data)).catch(() => { setItems([]); toast.error("Impossible de charger les modèles"); });
  useEffect(() => { load(); }, []);

  const shown = filter ? (items || []).filter((t) => t.message_type === filter) : (items || []);

  const startEdit = (t) => { setEditing(t.template_id); setDraft({ name: t.name, content: t.content }); };
  const saveEdit = async () => {
    try {
      await api.patch(`/templates/${editing}`, draft);
      toast.success("Modèle mis à jour");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur mise à jour");
    }
  };
  const remove = async (id) => {
    if (!confirm("Supprimer ce modèle ?")) return;
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Modèle supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1300px]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-100">Modèles IA</h1>
          <p className="text-sm text-slate-400 mt-1">
            Vos meilleurs messages sauvegardés. Les variables <span className="font-mono text-cyan-300">{"{{entreprise}}"}</span>, <span className="font-mono text-cyan-300">{"{{contact}}"}</span>, <span className="font-mono text-cyan-300">{"{{prenom}}"}</span> et <span className="font-mono text-cyan-300">{"{{ville}}"}</span> sont remplacées automatiquement à l'application.
          </p>
        </div>
        <select
          data-testid="template-filter-select"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none focus:border-cyan-500/60"
        >
          <option value="">Tous les types</option>
          {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {items === null && <div data-testid="templates-loading" className="p-8 text-slate-500 font-mono text-sm">Chargement…</div>}

      {items !== null && shown.length === 0 && (
        <div data-testid="templates-empty" className="p-12 text-center bg-[#121215] border border-slate-800/80 rounded-xl">
          <BookMarked className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <div className="text-slate-300 font-medium">Aucun modèle pour l'instant</div>
          <div className="text-sm text-slate-500 mt-1">Générez un message sur une fiche prospect puis cliquez sur « Sauver comme modèle ».</div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {shown.map((t) => (
          <div key={t.template_id} data-testid={`template-card-${t.template_id}`} className="p-5 bg-[#121215] border border-slate-800/80 rounded-xl flex flex-col">
            <div className="flex items-start justify-between gap-3">
              {editing === t.template_id ? (
                <input
                  data-testid="template-name-input"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className="flex-1 px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none focus:border-cyan-500/60"
                />
              ) : (
                <div data-testid="template-name" className="font-heading font-semibold text-slate-100 text-lg leading-tight">{t.name}</div>
              )}
              <div className="flex items-center gap-1 shrink-0">
                {editing === t.template_id ? (
                  <>
                    <button data-testid="template-save-button" onClick={saveEdit} className="p-1.5 rounded-lg text-emerald-400 hover:bg-emerald-500/10"><Check className="w-4 h-4" /></button>
                    <button data-testid="template-cancel-button" onClick={() => setEditing(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-800"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <button data-testid="template-copy-button" onClick={() => { navigator.clipboard.writeText(t.content); toast.success("Copié"); }} className="p-1.5 rounded-lg text-slate-400 hover:text-cyan-300 hover:bg-slate-800"><Copy className="w-4 h-4" /></button>
                    <button data-testid="template-edit-button" onClick={() => startEdit(t)} className="p-1.5 rounded-lg text-slate-400 hover:text-cyan-300 hover:bg-slate-800"><Pencil className="w-4 h-4" /></button>
                    <button data-testid="template-delete-button" onClick={() => remove(t.template_id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${TYPE_COLOR[t.message_type] || "bg-slate-800 text-slate-300 border-slate-700"}`}>{TYPE_LABEL[t.message_type] || t.message_type}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.tone}</span>
              <span className="text-[10px] font-mono text-slate-600">· {t.model}</span>
              <span data-testid="template-usage" className="ml-auto text-[10px] font-mono uppercase tracking-widest text-amber-400">{t.usage_count} utilisation{t.usage_count > 1 ? "s" : ""}</span>
            </div>

            {editing === t.template_id ? (
              <textarea
                data-testid="template-content-input"
                value={draft.content}
                onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                rows="10"
                className="mt-3 w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm font-mono outline-none focus:border-cyan-500/60"
              />
            ) : (
              <pre data-testid="template-content" className="mt-3 text-xs text-slate-300 whitespace-pre-wrap font-mono leading-relaxed max-h-48 overflow-y-auto p-3 rounded-lg bg-slate-900/40 border border-slate-800/60">{t.content}</pre>
            )}
            <div className="mt-3 text-[10px] font-mono uppercase text-slate-600">Créé le {new Date(t.created_at).toLocaleDateString("fr-FR")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
